<template>
  <div class="container py-8 mb-12 bg-white">
    <h2
      class="text-2xl font-semibold text-center font-myriad-pro-condensed"
      v-text="$t('common.available_inventories')"
    />
    <section v-if="hide === false" id="inventories" class="w-full py-6 mx-auto">
      <div v-if="!pending" class="w-full">
        <!-- Filters container -->
        <div class="filters__container">
          <!-- Filter column: dynamic  -->
          <div
            v-for="(attribute, attrIndex) in options"
            :key="attrIndex"
            class="filters__column"
          >
            <!-- Filter label -->
            <div class="filters__label" v-text="attribute.name" />

            <!-- Filter Action -->
            <div
              class="filters__cta"
              :class="{ 'is-color': attribute.slug === 'color' }"
            >
              <div v-for="value in attribute.values" :key="value.id">
                <div
                  v-if="attribute.slug === 'color'"
                  class="filters__item is-color"
                  :class="{ ring: selected[attribute.id] === value.id }"
                  @click.prevent="toggleAttributeFilter(attribute.id, value.id)"
                >
                  <ColorBall
                    :colors="[
                      value['settings'].color1,
                      value['settings'].color2,
                    ]"
                    class="pointer-events-none"
                    :size="24"
                    :name="value['content']"
                  />
                </div>
                <div
                  v-else
                  :class="{ ring: selected[attribute.id] === value.id }"
                  class="filters__item"
                  @click.prevent="toggleAttributeFilter(attribute.id, value.id)"
                  v-text="value['content']"
                />
              </div>
            </div>
          </div>

          <!-- Filter column: inventories statuses -->
          <div class="filters__column">
            <div
              class="filters__label"
              v-text="$t('common.inventory_status')"
            />
            <div class="filters__cta">
              <div
                v-for="status in state.inventoryStatuses"
                :key="status.key"
                :class="{ ring: selectedInventoryStatus === status.key }"
                class="filters__item"
                @click.prevent="toggleInventoryStatus(status.key)"
                v-text="status.text"
              />
            </div>
          </div>
        </div>

        <template v-if="items.length > 0">
          <ul class="px-3 mt-6 sm:px-6 items__wrapper">
            <li
              v-for="inventory in items"
              :key="inventory.uuid"
              class="flex-row justify-between block py-6 border-b border-bottom sm:flex sm:items-center sm:flex-column"
            >
              <div class="sm:flex sm:items-center order-0">
                <div
                  v-if="inventory.picture"
                  class="mb-8 md:me-6 md:mb-0 order-0"
                >
                  <img
                    class="mx-auto"
                    :alt="inventory.name"
                    :src="inventory.picture"
                  />
                </div>

                <div class="order-1">
                  <h4
                    class="text-base font-bold uppercase sm:text-xl text-start"
                    v-text="inventory.name"
                  />
                  <div class="mt-3 feature__list">
                    <div class="flex flex-wrap items-center space-s-1">
                      <CheckIcon
                        class="w-4 h-4 text-gray-300 sm:h-6 sm:w-6 order-0"
                      />
                      <span
                        class="order-1 text-xs tracking-wide text-gray-500 sm:text-sm"
                        v-text="
                          $t(`common.inventory_status_${inventory.status}`)
                        "
                      />
                    </div>
                    <div class="flex flex-wrap items-center space-s-1">
                      <TruckIcon
                        class="w-4 h-4 text-gray-300 sm:h-6 sm:w-6 order-0"
                      />
                      <span
                        class="order-1 text-xs tracking-wide text-gray-500 sm:text-sm"
                        v-text="$t(`common.free_shipping`)"
                      />
                    </div>
                    <div class="flex flex-wrap items-center space-s-1">
                      <LocationIcon
                        class="w-4 h-4 text-gray-300 sm:h-6 sm:w-6 order-0"
                      />
                      <span
                        class="order-1 text-xs tracking-wide text-gray-500 sm:text-sm"
                      >
                        {{ inventory.location.label }},
                        {{ inventory.location.state }}
                      </span>
                    </div>
                    <div class="flex flex-wrap items-center space-s-1">
                      <ClockIcon
                        class="w-4 h-4 text-gray-300 sm:h-6 sm:w-6 order-0"
                      />
                      <span
                        class="order-1 text-xs tracking-wide text-gray-500 sm:text-sm"
                        v-text="$t('common.etd')"
                      />

                      <span
                        class="text-xs tracking-wide text-gray-500 sm:text-sm"
                      >
                        {{
                          inventory.status === "on_hand"
                            ? inventory.local_etd
                            : inventory.total_etd
                        }}:
                        {{ $t("common.etd_days") }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex flex-col order-1 mt-4 sm:mt-0 sm:items-end">
                <strong
                  class="block mt-3 font-sans text-base font-black text-start sm:text-2xl"
                  >${{ inventory.final_price }}</strong
                >

                <a
                  class="flex items-center text-xs text-gray-500 underline cursor-pointer text-start space-s-1"
                  @click.prevent="(e) => openPaymentMethods()"
                >
                  <CurrencyIcon class="w-4 h-4 order-0" />
                  <span
                    class="order-1"
                    v-text="$t('common.view_payment_methods')"
                  />
                </a>

                <Btn
                  :key="inventory.uuid"
                  :disabled="
                    cartStore.cartItems.find((ci) => inventory.uuid == ci.uuid)
                      ? true
                      : false
                  "
                  size="xs"
                  class="block w-full mt-3 sm:w-auto"
                  color="primary"
                  @click.prevent="addItemToCart(inventory.uuid)"
                  v-text="$t('common.add_to_bag')"
                />
              </div>
            </li>
          </ul>
          <div class="flex items-center py-3 text-center">
            <div class="flex items-center mx-auto pagination">
              <Btn
                :disabled="!enablePrevPage"
                size="xs"
                color="white"
                @click="goPrevPage"
                v-text="$t('common.previous')"
              />
              <span
                class="px-3 py-2 text-gray-500"
                v-text="metaData.current_page"
              />
              <Btn
                :disabled="!enableNextPage"
                size="xs"
                color="white"
                @click="goNextPage"
                v-text="$t('common.next')"
              />
            </div>
          </div>
        </template>
        <template v-else>
          <div
            class="py-6 text-2xl text-center text-gray-500 font-italic"
            v-text="$t('common.no_inventories_found')"
          />
        </template>
      </div>
    </section>
  </div>
</template>

<script setup>
import PaymentMethods from "~/modals/PaymentMethods";
import TruckIcon from "~/assets/svg/heroicons/truck.svg?component";
import CurrencyIcon from "~/assets/svg/heroicons/currency.svg?component";
import CheckIcon from "~/assets/svg/heroicons/check.svg?component";
import LocationIcon from "~/assets/svg/heroicons/location.svg?component";
import ClockIcon from "~/assets/svg/heroicons/clock.svg?component";
import { useCartStore } from "@/store/cart";
import { useModal } from "vue-final-modal";
import { useTranslation } from "i18next-vue";

const props = defineProps({
  productSlug: {
    type: String,
    required: true,
  },
});

const cartStore = useCartStore();
const nuxtApp = useNuxtApp();
const { t } = useTranslation();

const hide = ref(false);
const loading = ref(true);
const items = ref([]);
const options = ref([]);
const selected = ref({});
const selectedInventoryStatus = ref(null);
const metaData = ref(null);
const state = reactive({
  inventoryStatuses: [
    {
      key: "on_hand",
      text: t("common.inventory_status_on_hand"),
    },
    {
      key: "in_transit",
      text: t("common.inventory_status_in_transit"),
    },
    {
      key: "in_production",
      text: t("common.inventory_status_in_production"),
    },
  ],
});

async function fetchApi() {
  try {
    const { data, pending } = await useAPI(
      `api/front/products/${props.productSlug}/variants/attributes`
    );

    options.value = data;
  } catch (error) {
    console.error(error);
  }
}

fetchApi();

const enablePrevPage = computed(() => {
  return metaData.value.current_page > 1;
});
const enableNextPage = computed(() => {
  return metaData.value.current_page < metaData.value.last_page;
});

onMounted(() => {
  if (process.browser) {
    loadInventories(1, true);
  }
});

const { open, close } = useModal({
  component: PaymentMethods,
});

const openPaymentMethods = open;

function addItemToCart(uuid) {
  const successCallback = () => {
    loadInventories();
    nuxtApp.$toast.clearAll();

    // nuxtApp.$toast.info(t("common.item_has_been_add_to_bag"),
    //     {
    //       text: t("common.checkout"),
    //       onClick: (e, toastObject) => {
    //         toastObject.goAway(0);
    //         router.push(
    //           nuxtApp.$i18nPath(`/checkout/${cartStore.cartUUID}`)
    //         );
    //       },
    //   }
    // );
  };

  cartStore
    .addCartItem({
      uuid,
      cb: successCallback,
    })
    .catch((error) => {
      if (error.response) {
        nuxtApp.$toast.error(error.response.data.message);
      }
    });
}

function goPrevPage() {
  if (enablePrevPage.value) {
    loadInventories(metaData.value.current_page - 1);
  }
}

function goNextPage() {
  if (enableNextPage.value) {
    loadInventories(metaData.value.current_page + 1);
  }
}

function toggleAttributeFilter(attributeId, valueId) {
  if (selected.value[attributeId] && selected.value[attributeId] === valueId) {
    delete selected.value[attributeId];
  } else {
    selected.value[attributeId] = valueId;
  }

  loadInventories();
}

function toggleInventoryStatus(status) {
  if (selectedInventoryStatus.value === status) {
    status = null;
  }
  selectedInventoryStatus.value = status;
  loadInventories();
}

async function loadInventories(page = 1, firstLoad = false) {
  loading.value = true;

  try {
    const { data, meta } = await useAPI(
      `api/front/products/${props.productSlug}/inventories`,
      {
        params: {
          page,
          values: selected.value,
          inventory_status: selectedInventoryStatus.value,
        },
      }
    );

    metaData.value = meta;
    items.value = data;

    if (firstLoad === true && items.value.length === 0) {
      hide.value = true;
    }

    setTimeout(() => {
      loading.value = false;
    }, 700);
  } catch (error) {
    console.error(error);
    setTimeout(() => {
      loading.value = false;
    }, 700);
  }
}
</script>

<style lang="postcss">
.filters__container {
  @apply flex flex-col space-y-6 px-3 py-3 sm:flex-row sm:items-center sm:space-s-6 sm:space-y-0 sm:py-6 sm:px-6 rounded border bg-gray-50;
}

.filters__column {
  /*@apply block w-full;*/
}

.filters__label {
  @apply tracking-wide text-sm text-gray-400 uppercase text-start;
}

.filters__cta {
  @apply inline-flex flex-col space-y-2 h-auto mt-1 sm:mt-4 sm:w-auto sm:flex sm:flex-row sm:items-center sm:space-y-0 sm:space-s-3 sm:h-6;

  &.is-color {
    @apply flex flex-row space-s-3 items-center space-y-0;
  }
}

.filters__item {
  @apply bg-white text-gray-500 rounded text-center px-4 py-2 ring-primary-500 ring-opacity-70 transition-all ease-in duration-150 rounded inline-block cursor-pointer text-xs  font-semibold shadow;
}

.filters__item.is-color {
  @apply rounded-full bg-transparent shadow-none p-0 !important;
}

.feature__list {
  @apply flex flex-col space-y-2;
}
[dir="rtl"] {
  .filters__container,
  .pagination {
    flex-direction: row-reverse;
  }

  .feature__list {
    flex-direction: column-reverse;

    div {
      align-self: flex-end;
    }
  }
}
</style>
